import { Box, Chip, Typography, alpha, useTheme } from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import moment from 'moment';
import type { Student } from '@/types/school';

function formatLoggedInAt(value?: string | null): string {
  if (!value) return '—';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('MMM D, YYYY, hh:mm A') : '—';
}

function LoggedInCell({ value }: GridRenderCellParams<Student, boolean>) {
  const theme = useTheme();
  const isLoggedIn = Boolean(value);

  return (
    <Chip
      size="small"
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: isLoggedIn
                ? theme.palette.success.main
                : theme.palette.grey[500],
            }}
          />
          {isLoggedIn ? 'Yes' : 'No'}
        </Box>
      }
      sx={{
        height: 22,
        fontWeight: 600,
        fontSize: '0.6875rem',
        backgroundColor: isLoggedIn
          ? alpha(theme.palette.success.main, 0.1)
          : alpha(theme.palette.grey[500], 0.12),
        color: isLoggedIn ? theme.palette.success.dark : theme.palette.text.secondary,
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  );
}

export const STUDENT_COLUMNS: GridColDef<Student>[] = [
  {
    field: 'fullName',
    headerName: 'Full Name',
    flex: 1,
    minWidth: 170,
    renderCell: (p) => (
      <Typography variant="body2" fontWeight={600}>{p.value}</Typography>
    ),
  },
  {
    field: 'phone',
    headerName: 'Phone Number',
    flex: 1,
    minWidth: 140,
    sortable: false,
    renderCell: (p) => (
      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', letterSpacing: '0.5px' }}>
        {p.value}
      </Typography>
    ),
  },
  {
    field: 'grade',
    headerName: 'Grade',
    flex: 1,
    minWidth: 110,
    renderCell: (p) => (
      <Typography variant="body2" color="text.secondary">{p.value}</Typography>
    ),
  },
  {
    field: 'username',
    headerName: 'Username',
    flex: 1,
    minWidth: 130,
    renderCell: (p) => (
      <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'text.primary' }}>
        {p.value ?? '—'}
      </Typography>
    ),
  },
  {
    field: 'userId',
    headerName: 'UAM ID',
    flex: 1,
    minWidth: 130,
    renderCell: (p) => (
      <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
        {p.value}
      </Typography>
    ),
  },
  {
    field: 'isLoggedIn',
    headerName: 'Logged In',
    flex: 1,
    minWidth: 110,
    renderCell: (p) => <LoggedInCell {...p} />,
  },
  {
    field: 'lastLoggedInTime',
    headerName: 'Last logged in at',
    flex: 1,
    minWidth: 180,
    valueFormatter: (value: string | null) => formatLoggedInAt(value),
    renderCell: (p) => (
      <Typography variant="caption" color="text.secondary">
        {formatLoggedInAt(p.value)}
      </Typography>
    ),
  },
];
